import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FileWarning, UserX, MessageSquareWarning, FileText,
  ExternalLink, ChevronRight
} from 'lucide-react';
import { useFinanceAnomalies, useDisputedReceipts } from '@/hooks/useFinanceAnalytics';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export default function AnomalyCards({ branchId }) {
  const navigate = useNavigate();
  const [disputesDialogOpen, setDisputesDialogOpen] = useState(false);

  const { data: anomalies, isLoading } = useFinanceAnomalies(branchId);
  const { data: disputedReceipts } = useDisputedReceipts(branchId);

  // Format currency
  const formatCurrency = (cents) => {
    if (!cents) return '0,00';
    return (cents / 100).toLocaleString('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Card click handlers
  const handleUnsentClick = () => {
    navigate('/Billing?tab=rodzice&filter=unsent');
  };

  const handleNoPricingClick = () => {
    navigate('/Billing?tab=rodzice&filter=no_costs');
  };

  const handleDisputesClick = () => {
    setDisputesDialogOpen(true);
  };

  const handleCreditNotesClick = () => {
    navigate('/Billing?tab=rodzice&filter=credit_notes');
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Card key={i}>
            <CardContent className="pt-4">
              <Skeleton className="h-8 w-8 rounded-lg mb-3" />
              <Skeleton className="h-6 w-12 mb-1" />
              <Skeleton className="h-4 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      id: 'unsent',
      label: 'Niewysłane rachunki',
      value: anomalies?.unsent_receipts_count || 0,
      icon: FileWarning,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      onClick: handleUnsentClick,
      isAlert: (anomalies?.unsent_receipts_count || 0) > 0
    },
    {
      id: 'no_pricing',
      label: 'Dzieci bez przypisanych kosztów',
      value: anomalies?.children_without_pricing || 0,
      icon: UserX,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      onClick: handleNoPricingClick,
      isAlert: (anomalies?.children_without_pricing || 0) > 0
    },
    {
      id: 'disputes',
      label: 'Otwarte reklamacje',
      value: anomalies?.open_disputes_count || 0,
      icon: MessageSquareWarning,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      onClick: handleDisputesClick,
      isAlert: (anomalies?.open_disputes_count || 0) > 0
    },
    {
      id: 'credit_notes',
      label: 'Korekty',
      value: anomalies?.credit_notes_count || 0,
      subValue: anomalies?.credit_notes_total_cents > 0
        ? `${formatCurrency(anomalies.credit_notes_total_cents)} PLN`
        : null,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: handleCreditNotesClick,
      isAlert: false
    }
  ];

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.id}
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                card.isAlert && "ring-2 ring-offset-2",
                card.isAlert && card.id === 'unsent' && "ring-orange-400",
                card.isAlert && card.id === 'no_pricing' && "ring-red-400",
                card.isAlert && card.id === 'disputes' && "ring-yellow-400"
              )}
              onClick={card.onClick}
            >
              <CardContent className="pt-4">
                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3", card.bgColor)}>
                  <Icon className={cn("w-5 h-5", card.color)} />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={cn(
                    "text-2xl font-bold",
                    card.isAlert ? card.color : "text-slate-900"
                  )}>
                    {card.value}
                  </span>
                  {card.subValue && (
                    <span className="text-sm text-slate-500">{card.subValue}</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-slate-500">{card.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Disputed Receipts Dialog */}
      <Dialog open={disputesDialogOpen} onOpenChange={setDisputesDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Otwarte reklamacje</DialogTitle>
            <DialogDescription>
              Lista rachunków z otwartymi reklamacjami
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto">
            {!disputedReceipts || disputedReceipts.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                Brak otwartych reklamacji
              </div>
            ) : (
              <div className="space-y-2">
                {disputedReceipts.map((dispute) => (
                  <div
                    key={dispute.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <div className="space-y-1">
                      <p className="font-medium text-slate-900">
                        {dispute.receipt?.child?.first_name} {dispute.receipt?.child?.last_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {dispute.receipt?.receipt_number} &bull; {formatCurrency(dispute.receipt?.total_amount_cents)} PLN
                      </p>
                      <p className="text-xs text-slate-400">
                        {dispute.reason || 'Brak podanego powodu'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        dispute.status === 'open' ? "bg-yellow-100 text-yellow-800" :
                        dispute.status === 'in_review' ? "bg-blue-100 text-blue-800" :
                        "bg-slate-100 text-slate-800"
                      )}>
                        {dispute.status === 'open' ? 'Otwarta' :
                         dispute.status === 'in_review' ? 'W trakcie' : dispute.status}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate(`/Billing?receipt=${dispute.receipt?.id}`);
                          setDisputesDialogOpen(false);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
